<template>
  <span class="calc-input-wrap" :class="{ 'calc-input-wrap-block': block }">
    <input
      ref="inputEl"
      type="text"
      inputmode="decimal"
      autocomplete="off"
      :class="inputClass"
      :style="inputStyle"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :tabindex="tabindex"
      :value="buffer"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="onKeydown"
      @compositionstart="composing = true"
      @compositionend="onCompositionEnd"
      @paste="onPaste"
    />
    <span
      v-if="showTape"
      class="calc-tape"
      :class="{ 'calc-tape-error': tapeError }"
      aria-hidden="true"
    >
      <template v-if="tapeError">{{ buffer }} = ?</template>
      <template v-else>{{ buffer }} = {{ formatDisplay(livePreview) }}</template>
    </span>
  </span>
</template>

<script>
import { evaluateExpression, isPlainNumber } from "@/helpers/calcParser";

export default {
  name: "CalcInput",
  props: {
    modelValue: { type: [Number, String, null], default: null },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    allowNegative: { type: Boolean, default: false },
    decimals: {
      type: Number,
      default: 2,
      validator: (v) => Number.isInteger(v) && v >= 0 && v <= 10,
    },
    min: { type: Number, default: null },
    max: { type: Number, default: null },
    inputClass: { type: String, default: "form-control form-control-sm text-end" },
    align: { type: String, default: "end" },
    block: { type: Boolean, default: false },
    emitNullOnEmpty: { type: Boolean, default: true },
    tabindex: { type: [String, Number], default: null },
  },
  emits: ["update:modelValue", "input", "commit", "keydown"],
  data() {
    return {
      buffer: "",
      focused: false,
      composing: false,
      livePreview: null,
      tapeError: false,
      lastValid: null,
    };
  },
  computed: {
    inputStyle() {
      return this.align ? { textAlign: this.align } : {};
    },
    showTape() {
      if (!this.focused) return false;
      if (!this.buffer) return false;
      if (isPlainNumber(this.buffer)) return false;
      return true;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(v) {
        if (this.focused) return;
        this.buffer = this.formatDisplay(v);
        this.lastValid = this.toNumberOrNull(v);
      },
    },
  },
  methods: {
    toNumberOrNull(v) {
      if (v === null || v === undefined || v === "") return null;
      const n = typeof v === "number" ? v : parseFloat(v);
      return Number.isFinite(n) ? n : null;
    },
    formatDisplay(v) {
      const n = this.toNumberOrNull(v);
      if (n === null) return "";
      return n.toFixed(this.decimals);
    },
    clampAndValidate(value) {
      if (!this.allowNegative && value < 0) {
        return { ok: false, error: "Negative not allowed" };
      }
      if (this.min !== null && value < this.min) {
        return { ok: false, error: `Minimum ${this.min}` };
      }
      if (this.max !== null && value > this.max) {
        return { ok: false, error: `Maximum ${this.max}` };
      }
      const rounded = Number(value.toFixed(this.decimals));
      return { ok: true, value: rounded };
    },
    setEmpty() {
      const nullVal = this.emitNullOnEmpty ? null : 0;
      this.livePreview = null;
      this.tapeError = false;
      this.emitIfChanged(nullVal);
      this.$emit("input", nullVal);
      return nullVal;
    },
    onInput(e) {
      this.buffer = e.target.value;
      if (this.composing) return;
      this.recomputeLive();
    },
    onCompositionEnd(e) {
      this.composing = false;
      this.buffer = e.target.value;
      this.recomputeLive();
    },
    recomputeLive() {
      if (this.buffer === "") {
        this.setEmpty();
        return;
      }
      const result = evaluateExpression(this.buffer);
      if (!result.ok) {
        this.livePreview = null;
        this.tapeError = true;
        return;
      }
      const clamped = this.clampAndValidate(result.value);
      if (!clamped.ok) {
        this.livePreview = null;
        this.tapeError = true;
        return;
      }
      this.livePreview = clamped.value;
      this.tapeError = false;
      this.emitIfChanged(clamped.value);
      this.$emit("input", clamped.value);
    },
    onFocus(e) {
      this.focused = true;
      this.$nextTick(() => {
        if (e.target && typeof e.target.select === "function") {
          e.target.select();
        }
      });
    },
    onBlur() {
      this.focused = false;
      this.commit({ reselectOnInvalid: false });
    },
    onKeydown(e) {
      this.$emit("keydown", e);
      if (e.defaultPrevented) return;
      if (e.key === "Enter" || e.key === "=") {
        e.preventDefault();
        this.commit({ reselect: e.key === "=" });
      }
    },
    onPaste(e) {
      const cd = e.clipboardData || window.clipboardData;
      if (!cd) return;
      const text = cd.getData("text");
      if (!text) return;
      // Always intercept so normalization happens on the first keystroke, not after.
      e.preventDefault();
      const el = this.$refs.inputEl;
      const start = el?.selectionStart || 0;
      const end = el?.selectionEnd || 0;
      const before = this.buffer.slice(0, start);
      const after = this.buffer.slice(end);
      this.buffer = before + text + after;
      this.$nextTick(() => {
        const newPos = start + text.length;
        el?.setSelectionRange(newPos, newPos);
      });
      this.recomputeLive();
    },
    commit({ reselect = false, reselectOnInvalid = true } = {}) {
      if (this.buffer === "" || this.buffer === null) {
        const nullVal = this.setEmpty();
        this.buffer = this.formatDisplay(nullVal);
        this.lastValid = nullVal;
        this.$emit("commit", nullVal);
        return;
      }
      const result = evaluateExpression(this.buffer);
      let clamped = null;
      if (result.ok) {
        clamped = this.clampAndValidate(result.value);
      }
      if (!result.ok || !clamped.ok) {
        this.buffer = this.formatDisplay(this.lastValid);
        this.tapeError = false;
        if (reselectOnInvalid) {
          this.$refs.inputEl?.focus();
        }
        return;
      }
      this.buffer = clamped.value.toFixed(this.decimals);
      this.emitIfChanged(clamped.value);
      this.lastValid = clamped.value;
      this.$emit("commit", clamped.value);
      if (reselect) {
        this.$nextTick(() => {
          this.$refs.inputEl?.select();
        });
      }
    },
    emitIfChanged(value) {
      const current = this.toNumberOrNull(this.modelValue);
      if (current !== value) {
        this.$emit("update:modelValue", value);
      }
    },
    focus() {
      this.$refs.inputEl?.focus();
    },
    select() {
      this.$refs.inputEl?.select();
    },
  },
};
</script>

<style scoped>
.calc-input-wrap {
  position: relative;
  display: inline-block;
}
.calc-input-wrap-block {
  display: block;
}
.calc-input-wrap input {
  width: 100%;
}
/* z-index matches Bootstrap modal-backdrop floor so tape stays above table rows. */
.calc-tape {
  position: absolute;
  top: calc(100% + 2px);
  right: 0;
  z-index: 1055;
  background: #0d1b2a;
  color: #ffe066;
  font-family: "Courier New", monospace;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 3px;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  pointer-events: none;
}
.calc-tape-error {
  background: #7a1f1f;
  color: #ffd6d6;
}
</style>
